<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { relativeDayTimestamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const isExpanded = ref(false);

const scheduledMessages = computed(() =>
  getters['scheduledMessages/getForConversation'].value(props.conversationId)
);
const pendingMessages = computed(() =>
  scheduledMessages.value.filter(message => message.status === 'pending')
);

const toUnixTime = isoString =>
  Math.floor(new Date(isoString).getTime() / 1000);

const statusColorClass = status => {
  return (
    {
      pending: 'bg-n-amber-9',
      sent: 'bg-n-teal-9',
      failed: 'bg-n-ruby-9',
      cancelled: 'bg-n-slate-8',
    }[status] || 'bg-n-slate-8'
  );
};

const fetchScheduledMessages = () => {
  if (props.conversationId) {
    store.dispatch('scheduledMessages/get', props.conversationId);
  }
};

const cancelScheduledMessage = async id => {
  try {
    await store.dispatch('scheduledMessages/cancel', {
      conversationId: props.conversationId,
      id,
    });
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.LIST.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.LIST.CANCEL_ERROR'));
  }
};

onMounted(fetchScheduledMessages);
watch(() => props.conversationId, fetchScheduledMessages);
</script>

<template>
  <div v-if="pendingMessages.length" class="px-4 pt-2">
    <button
      class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 hover:text-n-iris-9"
      @click="isExpanded = !isExpanded"
    >
      <i class="i-lucide-clock size-3.5" />
      {{
        t('CONVERSATION.SCHEDULE_MESSAGE.LIST.TITLE') +
        ` (${pendingMessages.length})`
      }}
      <i
        class="i-lucide-chevron-down size-3.5 transition-transform"
        :class="{ 'rotate-180': isExpanded }"
      />
    </button>
    <div v-if="isExpanded" class="flex flex-col gap-1.5 mt-2 mb-1">
      <div
        v-for="scheduledMessage in scheduledMessages"
        :key="scheduledMessage.id"
        class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-n-solid-1 border border-n-weak text-xs"
      >
        <span
          class="rounded-full size-2 shrink-0"
          :class="statusColorClass(scheduledMessage.status)"
        />
        <span class="flex-1 truncate text-n-slate-12">
          {{ scheduledMessage.content }}
        </span>
        <span class="text-n-slate-10 shrink-0">
          {{ relativeDayTimestamp(toUnixTime(scheduledMessage.scheduled_at)) }}
        </span>
        <button
          v-if="scheduledMessage.status === 'pending'"
          v-tooltip.top="t('CONVERSATION.SCHEDULE_MESSAGE.LIST.CANCEL_TOOLTIP')"
          class="text-n-slate-10 hover:text-n-ruby-11 shrink-0"
          @click="cancelScheduledMessage(scheduledMessage.id)"
        >
          <i class="i-lucide-x size-3.5" />
        </button>
      </div>
    </div>
  </div>
</template>
